/**
 * AI CloudHub (BRICK-ML) 라우터 설정
 *
 * 이 파일은 애플리케이션의 모든 라우팅과 네비게이션 가드를 관리합니다.
 *
 * 주요 기능:
 * 1. 사용자 인증 상태 검사 (토큰 유효성, 만료 시간)
 * 2. 권한 기반 접근 제어 (ADMIN/MEMBER)
 * 3. PRO/BASIC 모드에 따른 기능 제한
 * 4. OAuth 등 외부 인증 시스템 연동
 * 5. 클러스터 선택 상태 관리
 *
 * 인증 플로우:
 * 1. 토큰 존재 여부 확인
 * 2. 토큰 만료 시간 검사
 * 3. 사용자 권한 확인 (ADMIN/MEMBER)
 * 4. 페이지별 접근 권한 검사
 * 5. PRO 모드 기능 접근 제어
 *
 * 권한 체계:
 * - ADMIN: 시스템 관리자 (모든 기능 접근 가능)
 * - MEMBER: 일반 사용자 (제한된 기능만 접근 가능)
 *
 * 버전 구분:
 * - PRO: 모든 기능 사용 가능 (ML, Workflow, App 등)
 * - BASIC: 기본 기능만 사용 가능 (Container, Volume 등)
 */

/* eslint-disable no-undef */

import { createRouter, createWebHistory } from 'vue-router'
import { isTest } from '@/data/brickConfig'
import {
  checkTestRedirect,
  handleUrlTokenLogin,
  checkAuth,
  checkRootRedirect,
  checkCluster,
} from './routeGuards'
import routes from './routes'
import { prefetchPlotly } from '@/composables/usePlotlyLoader'
import { prefetchApexCharts } from '@/composables/useApexChartsLoader'
import { cleanRouteQuery } from '@/utils/routeQuery'
import { isChunkLikeError } from '@/utils/chunkError'

/**
 * Vue Router 설정
 *
 * 라우트 메타 정보 설명:
 * - auth: 접근 가능한 사용자 권한 배열 ['ADMIN', 'MEMBER']
 *   - ADMIN: 관리자만 접근 가능
 *   - MEMBER: 일반 사용자만 접근 가능
 *   - ['ADMIN', 'MEMBER']: 모든 사용자 접근 가능
 * - isPro: PRO 모드 전용 기능 여부
 *   - true: PRO 버전에서만 사용 가능한 기능
 *   - false: BASIC/PRO 모든 버전에서 사용 가능한 기본 기능
 */
const resolveBaseUrl = (rawBaseUrl) => {
  const raw = typeof rawBaseUrl === 'string' ? rawBaseUrl : ''
  if (!raw || /undefined|null/i.test(raw)) {
    return '/'
  }

  return raw.startsWith('/') ? raw : `/${raw}`
}

const baseUrl = resolveBaseUrl(import.meta.env.BASE_URL)
const ENABLE_ROUTE_WARMUP =
  import.meta.env.DEV && import.meta.env.VITE_ROUTE_WARMUP === 'true'
const ENABLE_CHART_PREFETCH =
  !import.meta.env.DEV || import.meta.env.VITE_CHART_PREFETCH === 'true'
const shouldPrefetchPlotlyForPath = (path) => {
  if (typeof path !== 'string') return false
  return (
    path.startsWith('/ml/run') ||
    path.startsWith('/ml/dataset/detail') ||
    path.startsWith('/ml/flow/detail') ||
    path.startsWith('/ml/flow/hpo')
  )
}

const APEXCHARTS_PREFETCH_PATH_PREFIXES = [
  '/dashboard',
  '/company-dashboard',
  '/project/detail',
  '/home/project/detail',
  '/container/detail',
  '/home/container/detail',
  '/management/node/detail',
]

const shouldPrefetchApexChartsForPath = (path) => {
  if (typeof path !== 'string') return false
  return APEXCHARTS_PREFETCH_PATH_PREFIXES.some((prefix) =>
    path.startsWith(prefix)
  )
}

let chartPrefetchTimer = null
const scheduleChartPrefetch = (path) => {
  if (!ENABLE_CHART_PREFETCH) return
  if (typeof window === 'undefined') return
  if (chartPrefetchTimer) {
    window.clearTimeout(chartPrefetchTimer)
  }

  chartPrefetchTimer = window.setTimeout(() => {
    chartPrefetchTimer = null
    if (shouldPrefetchPlotlyForPath(path)) {
      prefetchPlotly()
    }
    if (shouldPrefetchApexChartsForPath(path)) {
      prefetchApexCharts()
    }
  }, 250)
}

const isChunkImportError = (error) => isChunkLikeError(error)

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

const withLazyImportRetry = (loader) => {
  return () => {
    const loaded = loader()
    if (!loaded || typeof loaded.then !== 'function') {
      return loaded
    }

    return loaded.catch(async (error) => {
      if (!isChunkImportError(error) || typeof window === 'undefined') {
        throw error
      }

      // Vite optimizeDeps가 막 끝나는 타이밍에는 짧은 지연 후 재시도 시 성공하는 경우가 많다.
      await delay(350)
      return loader()
    })
  }
}

const stabilizeLazyRoutes = (routeRecords) => {
  if (!Array.isArray(routeRecords)) return

  routeRecords.forEach((routeRecord) => {
    if (typeof routeRecord?.component === 'function') {
      routeRecord.component = withLazyImportRetry(routeRecord.component)
    }
    if (Array.isArray(routeRecord?.children) && routeRecord.children.length > 0) {
      stabilizeLazyRoutes(routeRecord.children)
    }
  })
}

stabilizeLazyRoutes(routes)

const router = createRouter({
  history: createWebHistory(baseUrl),
  routes,
})

const isInvalidNavigationTarget = (target) => {
  if (target === null || target === undefined) return true

  if (typeof target === 'string') {
    const trimmed = target.trim()
    if (!trimmed) return true
    if (/undefined|null/i.test(trimmed)) return true
    if (!trimmed.startsWith('/')) return true
    return false
  }

  if (typeof target === 'object') {
    if (typeof target.path === 'string') {
      const trimmed = target.path.trim()
      if (!trimmed) return true
      if (/undefined|null/i.test(trimmed)) return true
      if (!trimmed.startsWith('/')) return true
      return false
    }

    if (Object.prototype.hasOwnProperty.call(target, 'path')) {
      return true
    }

    if (
      Object.prototype.hasOwnProperty.call(target, 'name') ||
      Object.prototype.hasOwnProperty.call(target, 'query') ||
      Object.prototype.hasOwnProperty.call(target, 'params') ||
      Object.prototype.hasOwnProperty.call(target, 'hash')
    ) {
      return false
    }
  }

  return false
}

const isInvalidResolvedRoute = (resolved) => {
  const path = typeof resolved?.path === 'string' ? resolved.path.trim() : ''
  const fullPath =
    typeof resolved?.fullPath === 'string' ? resolved.fullPath.trim() : ''
  const href = typeof resolved?.href === 'string' ? resolved.href.trim() : ''
  const combined = `${path} ${fullPath} ${href}`
  if (!path || !path.startsWith('/')) return true
  if (/undefined|null/i.test(combined)) return true
  return false
}

const sanitizeNavigationTarget = (target) => {
  if (!target) return target

  if (typeof target === 'string') {
    const [pathAndQuery, hash = ''] = target.split('#')
    const [path, queryString] = pathAndQuery.split('?')
    if (!queryString) return target

    const params = new URLSearchParams(queryString)
    const filtered = new URLSearchParams()
    params.forEach((value, key) => {
      if (value === 'undefined' || value === 'null') return
      filtered.append(key, value)
    })
    const nextQuery = filtered.toString()
    const nextHash = hash ? `#${hash}` : ''
    return nextQuery ? `${path}?${nextQuery}${nextHash}` : `${path}${nextHash}`
  }

  if (typeof target === 'object' && target.query && typeof target.query === 'object') {
    return { ...target, query: cleanRouteQuery(target.query) }
  }

  return target
}

const CHUNK_RELOAD_KEY = 'brickml:chunk-reload'
const CHUNK_RETRY_TARGET_KEY = 'brickml:chunk-retry-target'

const isValidFullPath = (value) => {
  if (typeof value !== 'string') return false
  const trimmed = value.trim()
  if (!trimmed) return false
  if (!trimmed.startsWith('/')) return false
  if (/undefined|null/i.test(trimmed)) return false
  return true
}

const shouldReloadForChunkError = (error) => isChunkLikeError(error)

const joinRoutePath = (parentPath, childPath) => {
  const parent = typeof parentPath === 'string' ? parentPath : ''
  const child = typeof childPath === 'string' ? childPath : ''
  if (!child) return parent || '/'
  if (child.startsWith('/')) return child
  if (!parent) return `/${child}`.replace(/\/{2,}/g, '/')
  return `${parent.replace(/\/$/, '')}/${child}`.replace(/\/{2,}/g, '/')
}

const shouldWarmRoutePath = (path) => {
  if (typeof path !== 'string' || !path.startsWith('/')) return false
  return (
    /\/detail(\/|$)/.test(path) ||
    path.startsWith('/ml/flow/map') ||
    path.startsWith('/container/detail/terminal')
  )
}

const collectWarmupLoaders = (routeRecords, parentPath = '') => {
  if (!Array.isArray(routeRecords)) return []

  return routeRecords.flatMap((record) => {
    const fullPath = joinRoutePath(parentPath, record?.path || '')
    const current = []
    if (shouldWarmRoutePath(fullPath) && typeof record?.component === 'function') {
      current.push({ path: fullPath, loader: record.component })
    }
    if (Array.isArray(record?.children) && record.children.length > 0) {
      return [...current, ...collectWarmupLoaders(record.children, fullPath)]
    }
    return current
  })
}

const warmupLazyRouteComponents = async () => {
  if (typeof window === 'undefined') return

  const uniqueLoaders = new Map()
  collectWarmupLoaders(routes).forEach((entry) => {
    if (!entry?.path || typeof entry.loader !== 'function') return
    if (!uniqueLoaders.has(entry.path)) {
      uniqueLoaders.set(entry.path, entry.loader)
    }
  })

  for (const [path, loader] of uniqueLoaders.entries()) {
    try {
      const loaded = loader()
      if (loaded && typeof loaded.then === 'function') {
        await loaded
      }
    } catch (error) {
      if (shouldReloadForChunkError(error)) {
        // optimizeDeps 재계산 타이밍에는 잠시 뒤 1회 재시도하면 대부분 복구된다.
        await new Promise((resolve) => window.setTimeout(resolve, 300))
        try {
          const retried = loader()
          if (retried && typeof retried.then === 'function') {
            await retried
          }
        } catch (retryError) {
          console.warn('[router] route warmup failed', { path, retryError })
        }
      }
    }
    await new Promise((resolve) => window.setTimeout(resolve, 80))
  }
}

const scheduleChunkNavigationRetry = (targetPath) => {
  if (typeof window === 'undefined') return false
  if (!targetPath) return false
  const retriedTarget = window.sessionStorage.getItem(CHUNK_RETRY_TARGET_KEY)
  if (retriedTarget === targetPath) return false
  window.sessionStorage.setItem(CHUNK_RETRY_TARGET_KEY, targetPath)
  // optimizeDeps 재번들링 직후 동일 경로를 재시도하면 사용자 재클릭 없이 복구된다.
  window.setTimeout(() => {
    router
      .replace(targetPath)
      .then((result) => {
        if (result === false) {
          const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`
          if (currentPath !== targetPath) {
            forceReloadForChunk(targetPath)
          }
        }
      })
      .catch((error) => {
        if (shouldReloadForChunkError(error)) {
          forceReloadForChunk(targetPath)
        }
      })
  }, 280)
  return true
}

const forceReloadForChunk = (targetPath) => {
  if (typeof window === 'undefined') return false
  const hasReloaded = window.sessionStorage.getItem(CHUNK_RELOAD_KEY)
  if (hasReloaded) {
    window.sessionStorage.removeItem(CHUNK_RELOAD_KEY)
    window.sessionStorage.removeItem(CHUNK_RETRY_TARGET_KEY)
    return false
  }

  window.sessionStorage.setItem(CHUNK_RELOAD_KEY, 'true')
  if (targetPath) {
    window.location.assign(targetPath)
    return true
  }

  window.location.reload()
  return true
}

const wrapNavigation = (methodName, originalFn) => {
  return (target, ...rest) => {
    const sanitizedTarget = sanitizeNavigationTarget(target)
    if (isInvalidNavigationTarget(sanitizedTarget)) {
      console.warn(`[router] blocked invalid ${methodName}`, sanitizedTarget)
      return Promise.resolve(false)
    }
    let resolvedFullPath = ''
    try {
      const resolved = router.resolve(sanitizedTarget)
      if (isInvalidResolvedRoute(resolved)) {
        console.warn(`[router] blocked invalid ${methodName} (resolved)`, {
          target: sanitizedTarget,
          resolved,
        })
        return Promise.resolve(false)
      }
      resolvedFullPath =
        typeof resolved?.fullPath === 'string' ? resolved.fullPath : ''
    } catch (error) {
      console.warn(`[router] failed to resolve ${methodName}`, {
        target: sanitizedTarget,
        error,
      })
      return Promise.resolve(false)
    }

    const result = originalFn(sanitizedTarget, ...rest)
    if (!result || typeof result.then !== 'function') {
      return result
    }

    return result.catch((error) => {
      if (!shouldReloadForChunkError(error)) {
        return Promise.reject(error)
      }
      const retryTarget = isValidFullPath(resolvedFullPath) ? resolvedFullPath : ''
      if (scheduleChunkNavigationRetry(retryTarget)) {
        return false
      }
      if (forceReloadForChunk(retryTarget)) {
        return false
      }
      return Promise.reject(error)
    })
  }
}

router.push = wrapNavigation('push', router.push.bind(router))
router.replace = wrapNavigation('replace', router.replace.bind(router))

router.onError((error, to) => {
  if (!shouldReloadForChunkError(error)) return
  if (typeof window === 'undefined') return

  const targetPath = isValidFullPath(to?.fullPath) ? to.fullPath : ''
  if (scheduleChunkNavigationRetry(targetPath)) {
    return
  }

  forceReloadForChunk(targetPath)
})

/**
 * 라우터 네비게이션 가드 - 모든 라우트 이동 시 실행되는 인증 및 권한 검사 로직
 *
 * 주요 검사 항목:
 * 1. 테스트 모드 처리
 * 2. URL 쿼리 파라미터를 통한 토큰 인증 (OAuth 등)
 * 3. 토큰 만료 검사
 * 4. 사용자 권한 검사 (ADMIN/MEMBER)
 * 5. PRO 모드 기능 접근 제어
 */
router.beforeEach(async (to, from) => {
  const rawPath = typeof to.path === 'string' ? to.path : ''
  const invalidPath =
    !rawPath ||
    !rawPath.startsWith('/') ||
    rawPath.includes('undefined') ||
    rawPath.includes('null')
  if (invalidPath) {
    console.warn('[router] blocked invalid navigation', {
      toPath: rawPath,
      toFullPath: to.fullPath,
      fromPath: from.fullPath,
    })
    return false
  }

  const resolveGuard = async (result) => {
    if (result && typeof result.then === 'function') {
      return await result
    }
    return result
  }

  // 1) 테스트 모드 처리
  const testRedirect = await resolveGuard(checkTestRedirect(to, isTest))
  if (testRedirect !== undefined) return testRedirect

  // 2) URL 토큰 로그인 처리
  const urlTokenHandled = await resolveGuard(handleUrlTokenLogin(to))
  if (urlTokenHandled !== undefined) return urlTokenHandled

  // 3) 인증/만료/권한 체크
  const authResult = await resolveGuard(checkAuth(to, from))
  if (authResult !== undefined) return authResult

  // 4) 루트 접근 시 리다이렉트
  const rootRedirect = await resolveGuard(checkRootRedirect(to))
  if (rootRedirect !== undefined) return rootRedirect

  // 5) 클러스터 필요 페이지 검사
  const clusterCheck = await resolveGuard(checkCluster(to))
  if (clusterCheck !== undefined) return clusterCheck

  return true
})

router.afterEach((to) => {
  const rawPath = typeof to.path === 'string' ? to.path : ''
  if (!rawPath || !rawPath.startsWith('/')) return
  if (typeof window !== 'undefined') {
    window.sessionStorage.removeItem(CHUNK_RETRY_TARGET_KEY)
  }
  scheduleChartPrefetch(rawPath)
})

router.isReady().then(() => {
  if (typeof window !== 'undefined') {
    window.sessionStorage.removeItem(CHUNK_RELOAD_KEY)
    window.sessionStorage.removeItem(CHUNK_RETRY_TARGET_KEY)

    if (ENABLE_ROUTE_WARMUP) {
      const scheduleWarmup =
        typeof window.requestIdleCallback === 'function'
          ? window.requestIdleCallback.bind(window)
          : (callback) => window.setTimeout(callback, 600)
      scheduleWarmup(() => {
        warmupLazyRouteComponents()
      })
    }
  }
})

export default router
